'use client';

import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import {
    ComponentProps,
    ComponentPropsWithoutRef,
    ReactNode,
    createContext,
    useContext,
    useEffect,
    useMemo,
    useRef,
} from 'react';
import { UserAccount } from '@/features/account/ui/user-account';
import { AddressItem, AddressSelect } from '@/features/address/ui/address-select';
import { QueryApi } from '@/features/query/api';
import { EventService } from '@/shared/lib/event-service';
import { Icon } from '@/shared/ui/icon';
import { Layout } from '@/shared/ui/layout';
import { LogoutButton } from '@/shared/ui/logout-button';
import { Modal } from '@/shared/ui/modal';
import { ModalService } from '@/shared/ui/modal/ModalService';
import { ModalProps } from '@/shared/ui/modal/types';
import { MyAccountModalMenuItem } from './my-account-modal-menu-item';
import './my-account-modal.scss';

type Props = ModalProps & {
    children?: ReactNode;
};

type MyAccountModalContextValue = {
    onClose: ModalProps['onClose'];
    customer: NonNullable<
        Awaited<ReturnType<NonNullable<(typeof QueryApi.Customer.queries.Customer)['queryFn']>>>
    >;
    addressesQuery: ReturnType<
        typeof useQuery<
            Awaited<
                ReturnType<NonNullable<ReturnType<typeof QueryApi.Customer.queries.Addresses>['queryFn']>>
            >
        >
    >;
    defaultAddressId?: string;
    handleEdit: () => void;
    handleShowFavoriteItems: () => void;
    handleShowOrdersHistory: () => void;
    handleAddressChange: (id: string | null) => void;
};

const MyAccountModalContext = createContext<MyAccountModalContextValue | null>(null);

const useMyAccountModalContext = () => {
    const context = useContext(MyAccountModalContext);
    if (!context) {
        throw new Error('MyAccountModal components must be used within MyAccountModalWrapper.');
    }
    return context;
};

const MyAccountModalProvider = ({ onClose, children }: Props) => {
    const customerQuery = useQuery(QueryApi.Customer.queries.Customer);
    const addressesQuery = useQuery(QueryApi.Customer.queries.Addresses({ offset: 0, limit: 10 }));
    const updateAddressMutation = useMutation(QueryApi.Customer.mutations.UpdateAddress);

    const prevCustomerQueryData = useRef(customerQuery.data);
    const customer = customerQuery.data ?? prevCustomerQueryData.current;

    const handleEdit = () => {
        ModalService.showPrefab('EditAccountDetailsModal', {});
    };
    const handleShowFavoriteItems = () => {
        EventService.trigger('account:favorite-items:view-detail');
    };
    const handleShowOrdersHistory = () => {
        ModalService.showPrefab('OrdersHistoryModal', {});
    };

    const handleAddressChange = (id: string | null) => {
        if (!id) {
            return;
        }
        const addressItem = addressesQuery.data?.items.find((a) => a.id === id);
        if (addressItem) {
            updateAddressMutation.mutate({
                addressId: id,
                title: addressItem.title,
                address: addressItem.address,
                icon: addressItem.icon,
                isDefault: true,
            });
        }
    };

    useEffect(() => {
        if (!customerQuery.data) {
            onClose();
        } else {
            prevCustomerQueryData.current = customerQuery.data;
        }
    }, [customerQuery.data, onClose]);

    const defaultAddressId = useMemo(
        () => addressesQuery.data?.items.find((a) => a.isDefault)?.id ?? addressesQuery.data?.items?.[0]?.id,
        [addressesQuery.data?.items],
    );

    if (!customer) {
        return null;
    }

    return (
        <MyAccountModalContext.Provider
            value={{
                onClose,
                customer,
                addressesQuery,
                defaultAddressId,
                handleEdit,
                handleShowFavoriteItems,
                handleShowOrdersHistory,
                handleAddressChange,
            }}
        >
            {children}
        </MyAccountModalContext.Provider>
    );
};

/** Provider that only provides context and renders children (Modal prefab). */
export const MyAccountModalProviderComponent = ({ children, ...props }: Props) => (
    <MyAccountModalProvider {...props}>{children}</MyAccountModalProvider>
);

export const MyAccountModalWrapper = ({ children, ...props }: Props) => {
    return (
        <MyAccountModalProvider {...props}>
            <Modal
                {...props}
                wrapperProps={{
                    className: 'my-account-modal',
                    containerProps: {
                        className: 'my-account-modal__container',
                    },
                    'data-testid': 'my-account-modal',
                }}
            >
                {children}
            </Modal>
        </MyAccountModalProvider>
    );
};

/** Renders only the title text. Use inside prefab: "Modal.Title". */
const MyAccountModalTitle = () => {
    const t = useTranslations();
    return <>{t('MY_ACCOUNT_MODAL.TITLE')}</>;
};

type UserAccountProps = ComponentProps<typeof Layout>;
const MyAccountModalUserAccount = ({ media, ...props }: UserAccountProps) => {
    const { customer, handleEdit } = useMyAccountModalContext();
    return (
        <Layout media={media} {...props}>
            <UserAccount
                user={{
                    firstName: customer.name,
                    lastName: customer.surname,
                    isPremiumMember: false,
                    isVerified: false,
                    profilePictureUrl: customer.avatarUrl,
                }}
                onClickEdit={handleEdit}
            />
        </Layout>
    );
};

type AddressSelectProps = ComponentProps<typeof Layout>;
const MyAccountModalAddressSelect = ({ media, ...props }: AddressSelectProps) => {
    const { addressesQuery, defaultAddressId, handleAddressChange } = useMyAccountModalContext();
    return (
        <Layout media={media} {...props}>
            <AddressSelect
                addresses={addressesQuery.data?.items as AddressItem[] | undefined}
                isLoading={addressesQuery.isLoading}
                defaultValue={defaultAddressId}
                onValueChange={handleAddressChange}
            />
        </Layout>
    );
};

type FavoriteItemsMenuItemProps = ComponentProps<typeof Layout>;
const MyAccountModalFavoriteItemsMenuItem = ({ media, ...props }: FavoriteItemsMenuItemProps) => {
    const t = useTranslations();
    const { handleShowFavoriteItems } = useMyAccountModalContext();
    return (
        <Layout
            element={MyAccountModalMenuItem}
            icon={(svgProps: ComponentPropsWithoutRef<'svg'>) => <Icon name="heart" {...svgProps} />}
            label={t('MY_ACCOUNT_MODAL.FAVORITE_ITEMS')}
            onClick={handleShowFavoriteItems}
            data-testid="my-account-modal-favorite-items-menu-item"
            media={media}
            {...props}
        />
    );
};

type OrdersHistoryMenuItemProps = ComponentProps<typeof Layout>;
const MyAccountModalOrdersHistoryMenuItem = ({ media, ...props }: OrdersHistoryMenuItemProps) => {
    const t = useTranslations();
    const { handleShowOrdersHistory } = useMyAccountModalContext();
    return (
        <Layout
            element={MyAccountModalMenuItem}
            icon={(svgProps: ComponentPropsWithoutRef<'svg'>) => <Icon name="history" {...svgProps} />}
            label={t('MY_ACCOUNT_MODAL.ORDER_HISTORY')}
            onClick={handleShowOrdersHistory}
            data-testid="my-account-modal-order-history-menu-item"
            media={media}
            {...props}
        />
    );
};

type LogoutProps = ComponentProps<typeof Layout>;
const MyAccountModalLogoutButton = ({ media, ...props }: LogoutProps) => {
    return (
        <Layout
            element={LogoutButton}
            media={media}
            data-testid="my-account-modal-logout-button"
            {...props}
        />
    );
};

export { MyAccountModalWrapper as MyAccountModal };

export {
    MyAccountModalWrapper as Wrapper,
    MyAccountModalProviderComponent as Provider,
    MyAccountModalTitle as Title,
    MyAccountModalUserAccount as UserAccount,
    MyAccountModalAddressSelect as AddressSelect,
    MyAccountModalFavoriteItemsMenuItem as FavoriteItemsMenuItem,
    MyAccountModalOrdersHistoryMenuItem as OrdersHistoryMenuItem,
    MyAccountModalLogoutButton as LogoutButton,
    MyAccountModalMenuItem as MenuItem,
};
